import snakeCase from "lodash/snakeCase";
import { useCallback, useEffect, useMemo, useState } from "react";

import { t } from "@/i18n-js/instance";
import type { FlaggedContentableType } from "@/react/types";
import {
  CHAT_ROOM_MESSAGE_TYPE,
  CONNECT_CONNECTION_TYPE,
} from "@/react/types/FlaggedContent";

import type { ModerationActionOptions } from "../../useModerationApi";

const i18nRoot = "settings.moderation.review_flagged_content_modal";

interface MenuOption {
  label: string;
  onClick: () => void;
}

interface ConditionalMenuOption extends MenuOption {
  when: boolean;
}

interface RejectState {
  isActive: boolean;
  options?: ModerationActionOptions;
  reason: string;
}

const DEFAULT_REJECT_STATE: RejectState = { isActive: false, reason: "" };

interface UseModerationFooterActionsParams {
  flaggedContentId: string;
  isAuthorFlagged?: boolean;
  isEmbeddedChat: boolean;
  isFlaggedForConnect?: boolean;
  onApprove: (options?: ModerationActionOptions) => void;
  onReject: (reason: string | null, options?: ModerationActionOptions) => void;
  status?: string;
  type?: FlaggedContentableType;
}

export const useModerationFooterActions = ({
  flaggedContentId,
  isAuthorFlagged,
  isFlaggedForConnect,
  isEmbeddedChat,
  status,
  type,
  onApprove,
  onReject,
}: UseModerationFooterActionsParams) => {
  const [rejectState, setRejectState] =
    useState<RejectState>(DEFAULT_REJECT_STATE);

  const resetRejectState = useCallback(() => {
    setRejectState(DEFAULT_REJECT_STATE);
  }, []);

  useEffect(() => {
    setRejectState({
      ...DEFAULT_REJECT_STATE,
      isActive: status === "approved",
    });
  }, [flaggedContentId, status]);

  const handleRejectWithOptions = useCallback(
    (options?: ModerationActionOptions) => {
      setRejectState(prev => ({ ...prev, isActive: true, options }));
    },
    [],
  );

  const handleRejectWithReason = useCallback(() => {
    onReject(rejectState.reason || null, rejectState.options);
  }, [onReject, rejectState.reason, rejectState.options]);

  const isPending = !status;
  const isConnection = type === CONNECT_CONNECTION_TYPE;
  const isChatMessage = type === CHAT_ROOM_MESSAGE_TYPE;

  const canFlagForConnect = isPending && isConnection && !isFlaggedForConnect;
  const canModifyConnections = isPending && isConnection;
  const canModifyChat = isPending && isChatMessage;
  const canFlagMember =
    isPending && !isConnection && !isChatMessage && !isAuthorFlagged;

  const toMenuOptions = (options: ConditionalMenuOption[]): MenuOption[] =>
    options.flatMap(({ when, ...option }) => (when ? [option] : []));

  const rejectMenuOptions = useMemo(
    () =>
      toMenuOptions([
        {
          when: canFlagForConnect,
          label: t([i18nRoot, "remove_and_flag_for_connect"]),
          onClick: () => handleRejectWithOptions({ flagForConnect: true }),
        },
        {
          when: canModifyConnections,
          label: t([i18nRoot, "remove_and_disable_connections"]),
          onClick: () => handleRejectWithOptions({ disableConnections: true }),
        },
        {
          when: canModifyChat && isEmbeddedChat,
          label: t([i18nRoot, "remove_and_disable_chat_messaging"]),
          onClick: () => handleRejectWithOptions({ flagForChat: true }),
        },
        {
          when: canModifyChat && !isEmbeddedChat,
          label: t([i18nRoot, "remove_and_disable_direct_messaging"]),
          onClick: () =>
            handleRejectWithOptions({ disableDirectMessaging: true }),
        },
        {
          when: canFlagMember,
          label: t([i18nRoot, "remove_and_flag_member"]),
          onClick: () => handleRejectWithOptions({ flagMember: true }),
        },
      ]),
    [
      canFlagForConnect,
      canModifyConnections,
      canModifyChat,
      canFlagMember,
      isEmbeddedChat,
      handleRejectWithOptions,
    ],
  );

  const approveMenuOptions = useMemo(
    () =>
      toMenuOptions([
        {
          when: canFlagForConnect,
          label: t([i18nRoot, "approve_and_flag_for_connect"]),
          onClick: () => onApprove({ flagForConnect: true }),
        },
        {
          when: canFlagMember,
          label: t([i18nRoot, "approve_and_flag_member"]),
          onClick: () => onApprove({ flagMember: true }),
        },
      ]),
    [canFlagForConnect, canFlagMember, onApprove],
  );

  const rejectLabel = type ? t([i18nRoot, `reject_${snakeCase(type)}`]) : "";

  return {
    rejectState,
    setRejectState,
    handleRejectWithOptions,
    handleRejectWithReason,
    rejectMenuOptions,
    approveMenuOptions,
    rejectLabel,
    resetRejectState,
  };
};
